/**
 * careforce.ts
 * Author: Arya Ingale
 * Date: 6 May 2026
 *
 * Sends a PDF proof to the customer via Salesforce Careforce API.
 * Uses native fetch (replaced axios) for the HTTP request.
 */
import {getToken} from "./getToken"
import {DesignServiceDependency, PdfProofActionTypes} from "../designService/designService"

const SALESFORCE_UPLOAD_TIMEOUT_MS = 30_000

export const sendPDFViaCareforce = async (binary: any, caseId: string, emailAddress: string) => {
	let tokenResponse

	try {
		tokenResponse = await getToken()
	} catch (error) {
		console.error("Failed to generate Careforce token:", (error as Error).message)
		DesignServiceDependency.logAction(PdfProofActionTypes.EmailProofFailure, {
			status: 502,
			message: "Token proxy failure",
		})
		throw new Error("Failed to generate the careforce token")
	}

	const headers = {
		"Content-Type": "application/json",
		Authorization: "Bearer " + tokenResponse.access_token,
	}

	const requestBody = {
		data: binary,
		customerEmail: emailAddress,
		recordId: caseId,
	}

	const controller = new AbortController()
	const timeoutId = setTimeout(() => controller.abort(), SALESFORCE_UPLOAD_TIMEOUT_MS)

	try {
		const response = await fetch(
			`${tokenResponse.instance_url}/services/apexrest/SendEmails/v1?requestor=CarePDFProof&action=PDFPROOF`,
			{
				method: "POST",
				headers,
				body: JSON.stringify(requestBody),
				signal: controller.signal,
			}
		)

		if (!response.ok) {
			let errorMessage = `Request failed with status ${response.status}`
			try {
				const data = await response.json()
				if (data.message) {
					errorMessage = `${data.message}${data.error ? ": " + data.error : ""}`
				} else if (Array.isArray(data) && data.length > 0 && data[0].message) {
					errorMessage = data[0].message
				}
			} catch {
				// Response body is not JSON, use default message
			}

			DesignServiceDependency.logAction(PdfProofActionTypes.EmailProofFailure, {
				status: response.status,
				message: `HTTP error ${response.status}`,
			})
			console.error("Failed to send Proof via email through careforce : " + errorMessage)
			return false
		}

		DesignServiceDependency.logAction(PdfProofActionTypes.EmailProof)
		return true
	} catch (error) {
		const err = error as any

		if (err?.name === "AbortError") {
			DesignServiceDependency.logAction(PdfProofActionTypes.EmailProofFailure, {
				status: 408,
				message: `Salesforce upload timed out after ${SALESFORCE_UPLOAD_TIMEOUT_MS}ms`,
			})
			console.error(
				`Failed to send Proof via email through careforce : Request timed out after ${SALESFORCE_UPLOAD_TIMEOUT_MS}ms`
			)
			return false
		}

		// Network-level error (no HTTP response available, e.g. DNS failure)
		// Log only a generic label — err.message may contain internal host/path details
		DesignServiceDependency.logAction(PdfProofActionTypes.EmailProofFailure, {
			status: err?.status ?? 500,
			message: "Network error",
		})
		console.error("Failed to send Proof via email through careforce : " + error)
		return false
	} finally {
		clearTimeout(timeoutId)
	}
}
